type Pair<a, b> = {
  first: a;
  second: b;
};

const pair = <a, b>(first: a, second: b): Pair<a, b> => ({first, second});

// We can’t properly represent the type of Effect in Flow, so we are crating
// these `any` types to use more like documentation.
type someValue = any;

// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
/**
 * `Effect` is a data structure modeling the control flow of
 * effectful computations and enables the separation between modeling effects
 * and interpreting them.
 * `io` represents the type of side-effects that can be performed.
 * `err` represents errors that the computation might fail with.
 * `ret` represents the final return value produced after running the effect.
 *
 * The Flow types are not very accurate but hopefully enough to understand the
 * shape of `Effect` values.
 *
 * Using a WFDL Union encoding (`{tag, val}`) in case we decide to implement
 * all of this directly in WFDL in the future.
 */
export type Effect<io, err, ret> =
  | {
      tag: 'Fail';
      val: err;
    }
  | {
      tag: 'Pure';
      val: ret;
    }
  | {
      tag: 'Impure';
      val: io;
    }
  | {
      tag: 'Bind';
      val: Pair<
        Effect<io, err, someValue>,
        (arg1: someValue) => Effect<io, err, ret>
      >;
    }
  | {
      tag: 'Apply';
      val: Pair<
        Effect<io, err, (arg1: someValue) => ret>,
        Effect<io, err, someValue>
      >;
    };

// Despite not being able to correctly define the type of `Effect` in Flow,
// the constructors below mostly have correct Flow type annotations.

/**
 * Create an effect that fails with the given error.
 */
export const Fail = <io, err, a>(val: err): Effect<io, err, a> => ({
  tag: 'Fail',
  val,
});

/**
 * Create an effect out of any value. When run, this effect will perform
 * no side-effects and return that value back.
 */
export const Pure = <io, err, a>(val: a): Effect<io, err, a> => ({
  tag: 'Pure',
  val,
});

/**
 * Create an effect out of a `io` action. When run, the effect is expected to
 * produce a value of type `a` that unfortunately is not reflected in the `io`
 * type because of Flow limitations.
 */
export const Impure = <io, err, a>(val: io): Effect<io, err, a> => ({
  tag: 'Impure',
  val,
});

/**
 * Given an effect and a continuation function, creates an effect that
 * when run will call the continuation with the return of `eff`.
 * `Bind` captures the essence of sequencing computations.
 */
export const Bind =
  <io, err, a>(
    eff: Effect<io, err, a>
  ): (<b>(arg1: (arg1: a) => Effect<io, err, b>) => Effect<io, err, b>) =>
  <b>(continuation: (arg1: a) => Effect<io, err, b>): Effect<io, err, b> => ({
    tag: 'Bind',
    val: pair(eff, continuation),
  });

/**
 * Given an effect returning a function and an effect returning a value, creates
 * an effect that will run the effects of both arguments (possibly in parallel)
 * and then apply the function to the value.
 * `Apply` captures the essence of parallel computations.
 */
export const Apply =
  <io, err, a, b>(
    effF: Effect<io, err, (arg1: a) => b>
  ): ((arg1: Effect<io, err, a>) => Effect<io, err, b>) =>
  (effX: Effect<io, err, a>): Effect<io, err, b> => ({
    tag: 'Apply',
    val: pair(effF, effX),
  });

// The Flow types for `matchEffect` and `EffectMatcher` are again not accurate.

type EffectMatcher<io, err, ret, o> = {
  Fail: (arg1: err) => o;
  Pure: (arg1: ret) => o;
  Impure: (arg1: io) => o;
  Bind: (
    arg1: Pair<
      Effect<io, err, someValue>,
      (arg1: someValue) => Effect<io, err, ret>
    >
  ) => o;
  Apply: (
    arg1: Pair<
      Effect<io, err, (arg1: someValue) => ret>,
      Effect<io, err, someValue>
    >
  ) => o;
};

export const matchEffect =
  <io, err, ret, o>(
    handlers: EffectMatcher<io, err, ret, o>
  ): ((arg1: Effect<io, err, ret>) => o) =>
  (eff: Effect<io, err, ret>) =>
    // @ts-expect-error don’t want to check each case individually
    handlers[eff.tag](eff.val);
